import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { IconQuestion, IconMessage, IconExternal, IconX } from './icons.jsx';
import { DEFAULT_CONTACT_LINKS } from '../config/siteText.js';
import { BETA_SURVEYS, helpContacts } from './helpFeedbackLinks.js';
import './helpFeedback.css';

const HelpFeedbackContext = createContext(null);

// One shared panel; no polling, persisted prompt, API call or automatic opening.
export function HelpFeedbackProvider({ children, contactLinks }) {
    const [open, setOpen] = useState(false);
    const location = useLocation();
    const openHelp = useCallback(() => setOpen(true), []);
    const closeHelp = useCallback(() => setOpen(false), []);
    useEffect(() => { closeHelp(); }, [location.pathname, closeHelp]);
    return <HelpFeedbackContext.Provider value={openHelp}>
        {children}
        {open && <HelpFeedbackSheet onClose={closeHelp} contactLinks={contactLinks} />}
    </HelpFeedbackContext.Provider>;
}

export function HelpFeedbackTrigger({ variant = 'page', className = '' }) {
    const openHelp = useContext(HelpFeedbackContext);
    return <button type="button" onClick={openHelp} aria-haspopup="dialog"
        className={`btl-help-trigger btl-help-trigger--${variant} ${className}`}>
        <span aria-hidden="true"><IconQuestion className="w-4 h-4" /></span>
        <span>Help &amp; Feedback</span>
    </button>;
}

export function HelpFeedbackSheet({ onClose, contactLinks }) {
    const dialog = useRef(null);
    const closeButton = useRef(null);
    const [section, setSection] = useState(null);
    useEffect(() => {
        const element = dialog.current;
        const origin = document.activeElement;
        element.showModal();
        closeButton.current.focus({ preventScroll: true });
        return () => {
            element.close();
            if (origin?.isConnected) origin.focus({ preventScroll: true });
        };
    }, []);
    const contacts = helpContacts(contactLinks, DEFAULT_CONTACT_LINKS);
    return createPortal(<dialog ref={dialog} className="btl-help-sheet selectable" aria-labelledby="btl-help-title"
        onKeyDown={event => {
            if (event.key === 'Escape') event.stopPropagation();
            if (event.key !== 'Tab') return;
            const controls = [...event.currentTarget.querySelectorAll('button, a[href]')]
                .filter(element => !element.disabled && element.getClientRects().length);
            const first = controls[0], last = controls[controls.length - 1];
            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault(); last?.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault(); first?.focus();
            }
        }}
        onCancel={event => { event.preventDefault(); onClose(); }}
        onClick={event => {
            if (event.target !== dialog.current) return;
            const r = dialog.current.getBoundingClientRect();
            if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) onClose();
        }}>
        <header>
            <div><small>BETWEEN THE LINES</small><h2 id="btl-help-title">Help &amp; Feedback</h2></div>
            <button type="button" ref={closeButton} aria-label="Close help and feedback" onClick={onClose}><IconX className="w-5 h-5" /></button>
        </header>
        <p className="btl-help-intro">How can we help?</p>
        <div className="btl-help-options">
            <button type="button" aria-expanded={section === 'help'} aria-controls="btl-help-support"
                onClick={() => setSection(section === 'help' ? null : 'help')}>
                <span aria-hidden="true"><IconMessage className="w-5 h-5" /></span>
                <span><strong>Get technical help</strong><small>Ask for a reply about a problem.</small></span>
            </button>
            <button type="button" aria-expanded={section === 'beta'} aria-controls="btl-help-beta"
                onClick={() => setSection(section === 'beta' ? null : 'beta')}>
                <span aria-hidden="true"><IconQuestion className="w-5 h-5" /></span>
                <span><strong>Beta feedback</strong><small>Share your trial experience.</small></span>
            </button>
        </div>
        <section id="btl-help-support" hidden={section !== 'help'} aria-label="Technical help">
            <p>Joining through a Beta invitation? Reply in the DM where you received it, or contact us below.</p>
            <div className="btl-help-links">{contacts.map(link => <a key={link.url} href={link.url}
                target={link.external ? '_blank' : undefined} rel={link.external ? 'noopener noreferrer' : undefined} referrerPolicy="no-referrer">
                <span><strong>{link.label}</strong><small>{link.display}{link.external ? ' · opens a new tab' : ''}</small></span>
                {link.external && <span aria-hidden="true"><IconExternal className="w-4 h-4" /></span>}
            </a>)}</div>
            <p className="btl-help-note">Tell us the page, what happened and your device. Please hide private details in screenshots and never send passwords or verification codes.</p>
        </section>
        <section id="btl-help-beta" hidden={section !== 'beta'} aria-label="Beta questionnaires">
            <p>For invited Anthology Beta testers. Have your Beta ID ready.</p>
            <div className="btl-help-links">{BETA_SURVEYS.map(survey => <a key={survey.id} href={survey.url} target="_blank" rel="noopener noreferrer" referrerPolicy="no-referrer">
                <span><strong>{survey.title}</strong><small>{survey.detail} · opens a new tab</small></span>
                <span aria-hidden="true"><IconExternal className="w-4 h-4" /></span>
            </a>)}</div>
            <p className="btl-help-note">Need a reply about a problem? Choose Get technical help above.</p>
        </section>
    </dialog>, document.body);
}
